import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 30000;
export const NOTIFY_REFRESH = "dsite-notifications-refresh";

export function refreshAdminNotifications() {
  window.dispatchEvent(new Event(NOTIFY_REFRESH));
}

function emptyInbox() {
  return { items: [], action_items: [], info_items: [], action_count: 0, info_unread: 0, badge: 0 };
}

function markInfoReadLocal(prev, nid) {
  const now = new Date().toISOString();
  const info_items = (prev.info_items || []).map((n) => (
    n.id === nid ? { ...n, read: true, read_at: n.read_at || now } : n
  ));
  const info_unread = info_items.filter((n) => !n.read).length;
  return {
    ...prev,
    info_items,
    items: (prev.items || []).map((n) => (n.id === nid ? { ...n, read: true, read_at: n.read_at || now } : n)),
    info_unread,
    badge: (prev.action_count || 0) + info_unread,
  };
}

function formatWhen(iso) {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return String(iso);
  const diff = Date.now() - t.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "방금";
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  return t.toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function rowTitle(n) {
  return n.display_title || n.title || "";
}

function rowMessage(n) {
  return n.display_message || n.body || "";
}

function rowActionLabel(n) {
  return n.action_label || "처리하러 가기";
}

export default function NotificationBell({ scope = "admin", onItemClick, panelPlacement = "end" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [inbox, setInbox] = useState(emptyInbox);
  const [panel, setPanel] = useState("action");
  const [asideStyle, setAsideStyle] = useState(undefined);
  const path = scope === "admin" ? "/admin/notifications/inbox" : "/customer/notifications";
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(path);
      if (scope === "admin") {
        const data = r.data || emptyInbox();
        setInbox({
          items: data.items || [],
          action_items: data.action_items || [],
          info_items: data.info_items || [],
          action_count: data.action_count || 0,
          info_unread: data.info_unread || 0,
          badge: data.badge || 0,
        });
      } else {
        setItems(Array.isArray(r.data) ? r.data : []);
      }
    } catch {
      /* ignore transient errors */
    }
  }, [path, scope]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onRefresh = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener(NOTIFY_REFRESH, onRefresh);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener(NOTIFY_REFRESH, onRefresh);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useLayoutEffect(() => {
    if (!open || panelPlacement !== "aside") {
      setAsideStyle(undefined);
      return undefined;
    }
    const place = () => {
      const btn = ref.current?.querySelector('[data-testid="notif-bell"]');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const panelW = Math.min(416, window.innerWidth - 24);
      let left = r.right + 8;
      if (left + panelW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - 8 - panelW);
      }
      setAsideStyle({
        position: "fixed",
        top: Math.max(8, r.top),
        left,
        zIndex: 90,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, panelPlacement]);

  const badge = scope === "admin"
    ? inbox.badge
    : items.filter((i) => !i.read).length;

  const markRead = async (nid) => {
    if (!nid || String(nid).startsWith("live:")) return;
    await api.patch(`/notifications/${nid}/read`);
  };
  const markAll = async (e) => {
    e?.stopPropagation();
    if (scope === "admin") {
      setInbox((prev) => {
        const now = new Date().toISOString();
        const info_items = (prev.info_items || []).map((n) => ({ ...n, read: true, read_at: n.read_at || now }));
        return { ...prev, info_items, info_unread: 0, badge: prev.action_count || 0 };
      });
    } else {
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    }
    await api.patch("/notifications/read-all");
    load();
  };

  const handleItemClick = async (n) => {
    const isAction = n.notification_type === "ACTION_REQUIRED";
    setOpen(false);
    if (!isAction) {
      setInbox((prev) => markInfoReadLocal(prev, n.id));
      setItems((prev) => prev.map((row) => (row.id === n.id ? { ...row, read: true } : row)));
      try {
        await markRead(n.id);
      } catch {
        /* local state already updated; refetch below */
      }
      load();
    }
    onItemClick?.(n);
  };

  const actionItems = inbox.action_items || [];
  const infoItems = inbox.info_items || [];
  const shown = scope === "admin"
    ? (panel === "action" ? actionItems : infoItems)
    : items;
  const showMarkAll = scope === "admin"
    ? inbox.info_unread > 0
    : items.some((n) => !n.read);

  const emptyCopy = scope === "admin" && panel === "action"
    ? "현재 처리할 업무가 없습니다."
    : "새로운 알림이 없습니다.";

  const panelClass = panelPlacement === "aside"
    ? "admin-notif-panel admin-notif-panel--aside bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden"
    : "admin-notif-panel absolute right-0 mt-2 bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden";
  const showPanel = open && (panelPlacement !== "aside" || asideStyle);

  return (
    <div ref={ref} className="relative z-40">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="notif-bell"
        className="relative w-9 h-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white">
        <Bell size={14}/>
        {badge > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-[#0055FF] text-white text-[9px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>
      {showPanel && (
        <div
          data-testid="notif-panel"
          className={panelClass}
          style={panelPlacement === "aside" ? asideStyle : undefined}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {scope === "admin" ? (
            <div className="px-3 py-2 flex items-center gap-1 border-b border-white/10">
              <button type="button" data-testid="notif-tab-action"
                onClick={() => setPanel("action")}
                className={`text-[11px] rounded-full px-2.5 py-1 ${panel === "action" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                처리 필요 {inbox.action_count}
              </button>
              <button type="button" data-testid="notif-tab-info"
                onClick={() => setPanel("info")}
                className={`text-[11px] rounded-full px-2.5 py-1 ${panel === "info" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                알림 {inbox.info_unread}
              </button>
              {showMarkAll && (
                <button
                  type="button"
                  data-testid="notif-mark-all"
                  onClick={markAll}
                  className="ml-auto text-[10px] text-white/50 hover:text-white shrink-0"
                >
                  모두 읽음
                </button>
              )}
            </div>
          ) : (
            <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
              <span className="text-sm font-medium">알림</span>
              {showMarkAll && (
                <button type="button" data-testid="notif-mark-all" onClick={markAll} className="text-[10px] text-white/50 hover:text-white">모두 읽음</button>
              )}
            </div>
          )}
          <div className="max-h-[28rem] overflow-y-auto">
            {shown.length === 0 ? (
              <div className="p-6 text-center text-xs text-white/40" data-testid="notif-empty">{emptyCopy}</div>
            ) : shown.map((n) => {
              const isAction = n.notification_type === "ACTION_REQUIRED";
              const unreadInfo = !isAction && !n.read;
              return (
                <button
                  type="button"
                  key={n.id}
                  data-testid={`notif-row-${n.id}`}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] ${unreadInfo ? "bg-[#0055FF]/[0.08] border-l-2 border-l-[#0055FF]" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {(isAction || unreadInfo) && <span className="w-1.5 h-1.5 rounded-full bg-[#0055FF] shrink-0"/>}
                      <div className={`text-xs ${unreadInfo ? "font-semibold text-white" : "font-medium text-white"}`}>{rowTitle(n)}</div>
                    </div>
                    {n.status_label && (
                      <span className="text-[10px] text-white/40 shrink-0">{n.status_label}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/65 mt-1 whitespace-pre-line">{rowMessage(n)}</div>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <span className="text-[10px] text-white/35">{formatWhen(n.created_at)}</span>
                    {isAction && (
                      <span className="text-[10px] text-[#7eb6ff]">{rowActionLabel(n)}</span>
                    )}
                    {!isAction && unreadInfo && (
                      <span className="text-[10px] text-white/40">안읽음</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
